import { useEffect, useState } from 'react';
import { filter, FilterProps } from '@/utils/exercise-filter';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Drawer, IconButton, InputAdornment, MenuItem, Paper, Select, Stack, TextField, Typography, useTheme } from '@mui/material';
import { ExerciseDTO, PlanUnit, PlanWorkout, DeckDTO, TrainingPlan } from '@/generated';
import { MutationOperation, MutationType, useUpdatePlan } from '@/hooks/use-update-plan.hook';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import ContentPasteOffIcon from '@mui/icons-material/ContentPasteOff';
import { Add, Close, Delete, Search, SearchOff, Update } from '@mui/icons-material';
import { filterBodyzones } from '@/utils/bodyzone-filter';
import { ExerciseConfigComponent } from './ExerciseConfigComponent';
import { DrawerExercise } from './DrawerExercise';
import ExerciseCategoryAutocomplete from '@/screens/exercise-categories/ExerciseCategoryAutocomplete';
import EquipmentAutocomplete from '@/screens/equipment/EquipmentAutocomplete';
import { useBodyZoneListQuery } from '@/hooks/query-hooks';

type AddExercisesDrawerProps = {
  deck: DeckDTO;
  exerciseIndex?: number;
  roundIndex?: number;
  show: boolean;
  selectedExercise?: ExerciseDTO;
  setShow: (show: boolean) => void;
  exerciseList: Array<ExerciseDTO>;
  plan?: TrainingPlan;
  unit?: PlanUnit;
  workout?: PlanWorkout;
};

const SHOW_SEARCH_AND_FILTER_STORAGE_KEY = 'addExerciseDrawerShowSearchAndFilter';
const SHOW_EXERCISE_CONFIGURATOR_STORAGE_KEY = 'addExerciseDrawerShowExerciseConfigurator';

const getStoredBoolean = (key: string, fallback: boolean) => {
  const storedValue = localStorage.getItem(key);
  return storedValue === null ? fallback : storedValue === 'true';
};

export const AddExercisesDrawer = (props: AddExercisesDrawerProps) => {
  const { exerciseIndex, roundIndex, plan, workout, unit } = props;
  const updatePlan = useUpdatePlan(plan, workout, unit);
  const { bodyZoneList } = useBodyZoneListQuery();
  const [filterProps, setFilterProps] = useState<FilterProps>();
  const [filteredExercises, setFilteredExercises] = useState<Array<ExerciseDTO>>();
  const [selectedExercise, setSelectedExercise] = useState<ExerciseDTO | undefined>(props.selectedExercise);
  const [showSearchAndFilter, setShowSearchAndFilter] = useState(() => getStoredBoolean(SHOW_SEARCH_AND_FILTER_STORAGE_KEY, true));
  const [replaceRow, setReplaceRow] = useState<boolean>(true);
  const [mutateRow, setMutateRow] = useState<boolean>(false);
  const [mutationOperation, setMutationOperation] = useState<MutationOperation>(MutationOperation.Increment);
  const [showExerciseConfigurator, setShowExerciseConfigurator] = useState(() => getStoredBoolean(SHOW_EXERCISE_CONFIGURATOR_STORAGE_KEY, false));
  const theme = useTheme();

  useEffect(() => {
    setSelectedExercise(props.selectedExercise);
  }, [props.selectedExercise]);

  useEffect(() => {
    if (filterProps && props.exerciseList) setFilteredExercises(filter(props.exerciseList, filterProps));
    else setFilteredExercises(props.exerciseList);
  }, [filterProps, props.exerciseList, props.show]);

  useEffect(() => {
    localStorage.setItem(SHOW_SEARCH_AND_FILTER_STORAGE_KEY, String(showSearchAndFilter));
  }, [showSearchAndFilter]);

  useEffect(() => {
    localStorage.setItem(SHOW_EXERCISE_CONFIGURATOR_STORAGE_KEY, String(showExerciseConfigurator));
  }, [showExerciseConfigurator]);

  const hide = () => {
    setFilteredExercises(props.exerciseList);
    setSelectedExercise(undefined);
    props.setShow(false);
  };

  return (
    <>
      <Drawer anchor="right" open={props.show} onClose={() => hide()}>
        <Stack sx={{ alignItems: 'center', width: 500 }}>
          <Box sx={{ position: 'sticky', top: 0, width: '100%', zIndex: 1 }}>
            <Paper elevation={20}>
              <Stack direction={'row'} sx={{ mt: 1, mx: 2, mb: 2, background: theme.palette.grey[700], borderRadius: 2 }}>
                <IconButton
                  sx={{ mx: 1 }}
                  onClick={async () => {
                    if (roundIndex !== undefined && exerciseIndex !== undefined) await updatePlan({ roundIndex, exerciseIndex, replaceRow });
                    hide();
                  }}
                >
                  <Delete />
                </IconButton>
                <Stack direction={'row'} sx={{ mr: 2, ml: 'auto' }}>
                  <IconButton onClick={() => setShowSearchAndFilter(!showSearchAndFilter)}>{showSearchAndFilter ? <Search /> : <SearchOff />}</IconButton>
                  <IconButton onClick={() => setReplaceRow(!replaceRow)}>{replaceRow ? <ViewColumnIcon /> : <LooksOneIcon />}</IconButton>
                  <IconButton onClick={() => setMutateRow(!mutateRow)} disabled={!replaceRow}>
                    {mutateRow ? <AutoGraphIcon /> : <LinearScaleIcon />}
                  </IconButton>
                  <IconButton
                    disabled={!replaceRow || !mutateRow}
                    onClick={() =>
                      mutationOperation === MutationOperation.Increment ? setMutationOperation(MutationOperation.Decrement) : setMutationOperation(MutationOperation.Increment)
                    }
                  >
                    {mutationOperation === MutationOperation.Increment ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                  </IconButton>
                  <IconButton onClick={() => setShowExerciseConfigurator(!showExerciseConfigurator)}>
                    {showExerciseConfigurator ? <ContentPasteOffIcon /> : <ContentPasteIcon />}
                  </IconButton>
                </Stack>
              </Stack>
              {showSearchAndFilter && (
                <Stack direction={'column'} sx={{ gap: 3, alignContent: 'dl', py: 3, mx: 2 }}>
                  <Stack style={{ flexDirection: 'row', gap: 20 }}>
                    <TextField
                      value={filterProps?.name}
                      sx={{}}
                      label="Name"
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="start">
                              <Close sx={{ cursor: 'pointer' }} onClick={() => setFilterProps({ ...filterProps, name: '' })} />
                            </InputAdornment>
                          ),
                        },
                      }}
                      onChange={(e: any) => {
                        setFilterProps({ ...filterProps, name: e.target.value });
                      }}
                    />
                    <EquipmentAutocomplete
                      selectedEquipment={filterProps?.equipmentIds?.map((id) => ({ equipmentId: id, optional: false })) ?? []}
                      setSelectedEquipment={(eq) => setFilterProps({ ...filterProps, equipmentIds: eq.map((e) => e.equipmentId) })}
                    />
                  </Stack>
                  <Stack style={{ flexDirection: 'row', gap: 20 }}>
                    <ExerciseCategoryAutocomplete
                      selectedCategoryIds={filterProps?.categoryIds ?? []}
                      setSelectedCategoryIds={(ids) => setFilterProps({ ...filterProps, categoryIds: ids })}
                    />
                    <Select
                      value={filterProps?.bodyZoneId !== '' && filterProps?.bodyZoneId ? filterProps?.bodyZoneId : 'any'}
                      onChange={(e) => {
                        if (e.target.value !== 'any') setFilterProps({ ...filterProps, bodyZoneId: e.target.value as any });
                        else setFilterProps({ ...filterProps, bodyZoneId: '' });
                      }}
                    >
                      <MenuItem key={'any'} value={'any'}>
                        Any Zone
                      </MenuItem>

                      {filterBodyzones(bodyZoneList ?? []).map((bz) => (
                        <MenuItem key={bz.id} value={bz.id}>
                          {bz.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </Stack>
                </Stack>
              )}
              {showExerciseConfigurator ? (
                <Stack sx={{ mx: 1 }}>
                  <ExerciseConfigComponent
                    exerciseConfig={selectedExercise?.config}
                    updateExerciseConfig={(tc) => {
                      if (selectedExercise) setSelectedExercise({ ...selectedExercise, config: tc });
                    }}
                  />
                  <Stack direction={'row'}>
                    <IconButton
                      sx={{ mb: 1 }}
                      disabled={selectedExercise === undefined}
                      onClick={async () => {
                        if (roundIndex !== undefined && exerciseIndex !== undefined)
                          await updatePlan({
                            roundIndex,
                            exerciseConfig: selectedExercise?.config,
                            exerciseIndex,
                            selectedExercise: selectedExercise,
                            replaceRow: replaceRow,
                            rowMutationParameter: mutateRow ? { op: mutationOperation, type: MutationType.Linear } : undefined,
                          });
                        hide();
                      }}
                    >
                      {selectedExercise?.id === props.selectedExercise?.id ? (
                        <>
                          <Update />
                          <Typography>&nbsp;Update Parameters</Typography>
                        </>
                      ) : (
                        <>
                          <Add />
                          <Typography>&nbsp;Add Customized</Typography>
                        </>
                      )}
                    </IconButton>
                  </Stack>
                </Stack>
              ) : (
                <Box sx={{ height: 2 }}></Box>
              )}
            </Paper>
          </Box>
          <Stack sx={{ width: '100%' }}>
            {filteredExercises?.map((e) => (
              <DrawerExercise
                onClick={() => {
                  setSelectedExercise(e);
                }}
                onAddClick={async () => {
                  if (roundIndex !== undefined && exerciseIndex !== undefined)
                    await updatePlan({
                      roundIndex,
                      exerciseConfig: e?.config,
                      exerciseIndex,
                      selectedExercise: e,
                      replaceRow: replaceRow,
                      rowMutationParameter: mutateRow ? { op: mutationOperation, type: MutationType.Linear } : undefined,
                    });
                  hide();
                }}
                key={e.id}
                exercise={e}
              />
            ))}
          </Stack>
        </Stack>
      </Drawer>
    </>
  );
};
